import { type ReactNode, Suspense } from 'react';
import type { TOptions } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { FrameLoadingTrigger } from '@interxfi/design-system/components/frame/context';
import type { TypographyProps } from '@interxfi/design-system/components/typography';
import { Typography } from '@interxfi/design-system/components/typography';
import type {
  I18nNamespaceEntity,
  I18nNamespaceEnum,
  I18nNamespaceFilterEnum,
  I18nNamespaceOneOf,
  I18nNamespacePolymorph,
  I18nNamespaceSearchEnum,
  I18nNamespaceSortEnum,
  I18nNamespaces,
} from '../generated';

const defaultTypographyProps: TypographyProps = {
  tone: 'inherit',
};

type EntityKeyObject = {
  namespace: 'entity';
  value: I18nNamespaceEntity | undefined;
  renderWhenValueMissing?: ReactNode;
  plural?: boolean;
};

type EnumKeyObject<N extends Exclude<I18nNamespaces, 'entity'>> = {
  namespace: N;
  enumName: I18nNamespaceEnum<N>;
  value: string | undefined;
  renderWhenValueMissing?: ReactNode;
};

type FilterEnumKeyObject<N extends Exclude<I18nNamespaces, 'entity'>> = {
  namespace: N;
  filterEnumName: I18nNamespaceFilterEnum<N>;
  value: string | undefined;
  renderWhenValueMissing?: ReactNode;
};

type SortEnumKeyObject<N extends Exclude<I18nNamespaces, 'entity'>> = {
  namespace: N;
  sortEnumName: I18nNamespaceSortEnum<N>;
  value: string | undefined;
  renderWhenValueMissing?: ReactNode;
};

type SearchEnumKeyObject<N extends Exclude<I18nNamespaces, 'entity'>> = {
  namespace: N;
  searchEnumName: I18nNamespaceSearchEnum<N>;
  value: string | undefined;
  renderWhenValueMissing?: ReactNode;
};

type OneOfKeyObject<N extends Exclude<I18nNamespaces, 'entity'>> = {
  namespace: N;
  oneOfName: I18nNamespaceOneOf<N>;
  value: string | undefined;
  renderWhenValueMissing?: ReactNode;
};

type PolymorphKeyObject<N extends Exclude<I18nNamespaces, 'entity'>> = {
  namespace: N;
  polymorphName: I18nNamespacePolymorph<N>;
  value: string | undefined;
  renderWhenValueMissing?: ReactNode;
};

export type I18nKey<TNamespace extends I18nNamespaces> =
  | `${TNamespace}:${string}`
  | (TNamespace extends 'entity'
      ? EntityKeyObject
      :
          | EnumKeyObject<Exclude<TNamespace, 'common' | 'entity'>>
          | OneOfKeyObject<Exclude<TNamespace, 'common' | 'entity'>>
          | PolymorphKeyObject<Exclude<TNamespace, 'common' | 'entity'>>
          | FilterEnumKeyObject<Exclude<TNamespace, 'common' | 'entity'>>
          | SortEnumKeyObject<Exclude<TNamespace, 'common' | 'entity'>>
          | SearchEnumKeyObject<Exclude<TNamespace, 'common' | 'entity'>>);

export interface TranslatedTextProps<TNamespace extends I18nNamespaces> {
  children?: ReactNode;
  defaultValue?: string;
  doNotWrap?: boolean;
  i18nKey: I18nKey<TNamespace>;
  typographyProps?: TypographyProps;
  variables?: TOptions;
}

function buildI18nKey<TNamespace extends I18nNamespaces>(i18nKey: I18nKey<TNamespace>): string {
  if (typeof i18nKey === 'string') {
    return i18nKey;
  }

  if (i18nKey.namespace === 'entity') {
    return `${i18nKey.namespace}:${i18nKey.value || 'default'}`;
  }

  if ('enumName' in i18nKey) {
    return `${i18nKey.namespace}:enum.${i18nKey.enumName}.${i18nKey.value || 'UNSPECIFIED'}`;
  }

  if ('polymorphName' in i18nKey) {
    return `${i18nKey.namespace}:polymorph.${i18nKey.polymorphName}.${i18nKey.value || 'default'}`;
  }

  if ('filterEnumName' in i18nKey) {
    return `${i18nKey.namespace}:filterEnum.${i18nKey.filterEnumName}.${i18nKey.value || 'UNSPECIFIED'}`;
  }

  if ('sortEnumName' in i18nKey) {
    return `${i18nKey.namespace}:sortEnum.${i18nKey.sortEnumName}.${i18nKey.value || 'UNSPECIFIED'}`;
  }

  if ('searchEnumName' in i18nKey) {
    return `${i18nKey.namespace}:searchEnum.${i18nKey.searchEnumName}.${i18nKey.value || 'UNSPECIFIED'}`;
  }

  if ('oneOfName' in i18nKey) {
    return `${i18nKey.namespace}:oneOf.${i18nKey.oneOfName}.${i18nKey.value || 'default'}`;
  }

  return '';
}

function getNamespace<TNamespace extends I18nNamespaces>(i18nKey: I18nKey<TNamespace>): string {
  if (typeof i18nKey === 'string') {
    return i18nKey.split(':')[0];
  }

  return i18nKey.namespace;
}

function getVariables<TNamespace extends I18nNamespaces, TOptions>(
  i18nKey: I18nKey<TNamespace>,
  defaultValue: string | undefined,
  variables: TOptions | undefined,
): TOptions {
  if (typeof i18nKey === 'string') {
    return variables || ({ defaultValue } as TOptions);
  }

  if (i18nKey.namespace === 'entity') {
    return { count: i18nKey.plural ? 2 : 1, ...(variables || { defaultValue }) } as TOptions;
  }

  return variables || ({ defaultValue } as TOptions);
}

function useTranslationProps<TNamespace extends I18nNamespaces>({
  children,
  defaultValue,
  i18nKey,
  variables,
  ...rest
}: TranslatedTextProps<TNamespace>) {
  const namespace = getNamespace(i18nKey);
  const { t } = useTranslation(namespace);
  const i18nKeyString = buildI18nKey(i18nKey);
  const finalVars = getVariables(i18nKey, defaultValue, variables);

  return {
    ...rest,
    'data-i18n-key': i18nKeyString,
    children: children ? (
      <Trans i18nKey={i18nKeyString} t={t} values={finalVars}>
        {children}
      </Trans>
    ) : (
      t(i18nKeyString, finalVars)
    ),
  };
}

function TextInner<TNamespace extends I18nNamespaces>(props: TranslatedTextProps<TNamespace>) {
  const { typographyProps = defaultTypographyProps, doNotWrap, ...tProps } = useTranslationProps<TNamespace>(props);
  const { i18nKey } = props;

  if (typeof i18nKey === 'object' && i18nKey.value === undefined) {
    if (doNotWrap) {
      return i18nKey.renderWhenValueMissing || null;
    }

    return <Typography {...typographyProps}>{i18nKey.renderWhenValueMissing || null}</Typography>;
  }

  if (doNotWrap) {
    return tProps.children || null;
  }

  return <Typography {...typographyProps} {...tProps} />;
}

export function TranslatedText<TNamespace extends I18nNamespaces>(props: TranslatedTextProps<TNamespace>) {
  return (
    <Suspense fallback={<FrameLoadingTrigger />}>
      <TextInner<TNamespace> {...props} />
    </Suspense>
  );
}
